import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { inviteAPI } from '../lib/api';
import { Users, Building2, ShieldCheck, Lock, CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';

interface InviteInfo {
    user_type: string;
    email: string;
    username: string;
    expires_at: string;
    is_valid: boolean;
    fiat_currency?: 'RUB' | 'KGS' | 'KZT';
}

export const InviteCompletePage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    
    const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

    useEffect(() => {
        const fetchInviteInfo = async () => {
            if (!token) {
                setError('Токен приглашения не указан');
                setIsLoading(false);
                return;
            }

            try {
                const data = await inviteAPI.getInvite(token);
                setInviteInfo(data);
            } catch (err: any) {
                setError(err.response?.data?.detail || 'Приглашение не найдено или недействительно');
            } finally {
                setIsLoading(false);
            }
        };

        fetchInviteInfo();
    }, [token]);

    const getUserTypeInfo = (userType: string) => {
        switch (userType) {
            case 'trader':
                return {
                    icon: <Users className="h-8 w-8 text-blue-600 dark:text-blue-300" />,
                    title: `Трейдер · ${inviteInfo?.fiat_currency || 'RUB'} / USDT`,
                    color: 'blue',
                    bgColor: 'bg-blue-100 dark:bg-blue-950',
                };
            case 'merchant':
                return {
                    icon: <Building2 className="h-8 w-8 text-green-600 dark:text-green-300" />,
                    title: `Мерчант · ${inviteInfo?.fiat_currency || 'RUB'} / USDT`,
                    color: 'green',
                    bgColor: 'bg-green-100 dark:bg-green-950',
                };
            case 'support':
                return {
                    icon: <ShieldCheck className="h-8 w-8 text-purple-600 dark:text-purple-300" />,
                    title: 'Саппорт',
                    color: 'purple',
                    bgColor: 'bg-purple-100 dark:bg-purple-950',
                };
            default:
                return {
                    icon: <Users className="h-8 w-8 text-gray-600 dark:text-slate-300" />,
                    title: 'Пользователь',
                    color: 'gray',
                    bgColor: 'bg-gray-100 dark:bg-slate-800',
                };
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const minimumPasswordLength = inviteInfo?.user_type === 'merchant' ? 10 : 6;
        if (password.length < minimumPasswordLength) {
            setError(`Пароль должен содержать минимум ${minimumPasswordLength} символов`);
            return;
        }

        if (password !== confirmPassword) {
            setError('Пароли не совпадают');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const result = await inviteAPI.completeRegistration(token!, password);
            setSuccess(true);
            setRedirectUrl(result.redirect_url);
            
            setTimeout(() => {
                window.location.assign(result.redirect_url);
            }, 3000);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Ошибка при регистрации');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getTimeRemaining = () => {
        if (!inviteInfo?.expires_at) return null;
        const expires = new Date(inviteInfo.expires_at);
        const now = new Date();
        const diff = expires.getTime() - now.getTime();
        
        if (diff <= 0) return 'Истекло';
        
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (hours > 0) {
            return `${hours}ч ${minutes}м`;
        }
        return `${minutes}м`;
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-blue-600 dark:text-blue-300 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-slate-300">Проверка приглашения...</p>
                </div>
            </div>
        );
    }

    if (error && !inviteInfo) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardHeader className="text-center">
                        <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-950 rounded-full flex items-center justify-center mb-4">
                            <XCircle className="h-8 w-8 text-red-600 dark:text-red-300" />
                        </div>
                        <CardTitle className="text-xl text-red-900 dark:text-red-200">Приглашение недействительно</CardTitle>
                        <CardDescription>{error}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button 
                            className="w-full" 
                            variant="outline"
                            onClick={() => navigate('/')}
                        >
                            Вернуться на главную
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardHeader className="text-center">
                        <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-950 rounded-full flex items-center justify-center mb-4">
                            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-300" />
                        </div>
                        <CardTitle className="text-xl text-green-900 dark:text-green-200">Регистрация завершена!</CardTitle>
                        <CardDescription>
                            Аккаунт успешно создан. Вы будете перенаправлены на страницу входа...
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button 
                            className="w-full" 
                            onClick={() => navigate(redirectUrl || '/')}
                        >
                            Перейти к входу
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const userTypeInfo = inviteInfo ? getUserTypeInfo(inviteInfo.user_type) : null;
    const minimumPasswordLength = inviteInfo?.user_type === 'merchant' ? 10 : 6;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center py-12 px-4">
            <Card className="max-w-md w-full">
                <CardHeader className="text-center">
                    {userTypeInfo && (
                        <div className={`mx-auto w-16 h-16 ${userTypeInfo.bgColor} rounded-full flex items-center justify-center mb-4`}>
                            {userTypeInfo.icon}
                        </div>
                    )}
                    <CardTitle className="text-2xl">Завершение регистрации</CardTitle>
                    <CardDescription>
                        Установите пароль для вашего аккаунта
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {inviteInfo && (
                        <div className="space-y-4 mb-6">
                            <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4 space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-500 dark:text-slate-400">Тип аккаунта:</span>
                                    <Badge variant="outline">
                                        {userTypeInfo?.title}
                                    </Badge>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-500 dark:text-slate-400">Имя пользователя:</span>
                                    <span className="font-medium">{inviteInfo.username}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-500 dark:text-slate-400">Email:</span>
                                    <span className="font-medium text-sm">{inviteInfo.email}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-500 dark:text-slate-400">Действует:</span>
                                    <span className="flex items-center gap-1 text-orange-600 dark:text-orange-300 text-sm">
                                        <Clock className="h-4 w-4" />
                                        {getTimeRemaining()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="password">Пароль</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder={`Минимум ${minimumPasswordLength} символов`}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="pl-10"
                                    required
                                    minLength={minimumPasswordLength}
                                />
                            </div>
                            <p className="text-xs text-gray-500 dark:text-slate-400">
                                Не менее {minimumPasswordLength} символов
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Подтвердите пароль</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    placeholder="Повторите пароль"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="pl-10"
                                    required
                                    minLength={minimumPasswordLength}
                                />
                            </div>
                        </div>

                        <Button 
                            type="submit" 
                            className="w-full"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Создание аккаунта...
                                </>
                            ) : (
                                'Завершить регистрацию'
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};
